import { store } from "@/store/store"
import {
	cullingInfoAtom,
	currentPhotoAtom,
	currentPhotoIdAtom,
	getPhotoMetadata,
	updatePhoto,
	updatePhotoMetadata,
} from "@/store/photo/photos-state"
import { atom } from "jotai"
import { throttleAsync } from "@/utils/throttle"
import { CatalogManager, PhotoRecord } from "@/lib/catalog"
import { startMeasurement } from "@/utils/benchmark"
import { getEditor } from "@/utils/useEditor"
import { adjustmentsHistoryAtom, updateWhiteBalanceSettingIfNeeded } from "@/store/store-history"
import { getFileForPhoto, getFileForPhotoFromOpfsIfNeeded } from "@/store/files-state"
import { isAbortError, isNotFoundError, isUnsupportedFileError } from "@/utils/error"
import { logPhotoError, showPhotoError } from "../photo/photo-error"
import { defaultMaskAdjustments, getDefaultAdjustments } from "@/core/adjustments"
import { isCalibrationAtom, isCullingModeAtom, projectAtom } from "../project/projects-state"
import { showUnsupportedTooltip } from "../app-state"
import { generateAutoExposedPreview, isCullingAutoExposureOnAtom } from "../importing/culling-state"
import { fetchOriginalPreviewDataOOB, fetchPreviewDataOOB } from "../previews/previews-processing"
import { OutputColorSpace } from "@/core/types"
import { PreviewSize } from "../previews/previews-types"
import { applyAIAdjustmentsAtom } from "../settings-state"
import { queueApplyAutoAdjustments } from "../processing/image-processing"
import { wait } from "@/utils/wait"
import { vec2 } from "gl-matrix"
import { photosViewAtom } from "../photo/photos-view-state"
import { PreviewLoader } from "@/lib/metadata/metadata-loader"

type PhotoLoadingState = "initial" | "loading" | "loaded" | "error"

export const photoLoadingStateAtom = atom<PhotoLoadingState>("initial")
export const photoLoadingErrorAtom = atom<Error | null>(null)
export const photoLoadedDimensionsAtom = atom<vec2 | null>(null)
export const isPhotoLoadedIntoEditorAtom = atom((get) => get(photoLoadedDimensionsAtom) !== null)
export const loadedPhotoCapabilitiesAtom = atom<{
	photoId: string
	isSourceMonochrome: boolean
} | null>(null)
export const isCurrentPhotoSourceMonochromeAtom = atom((get) => {
	const currentPhoto = get(currentPhotoAtom)
	const capabilities = get(loadedPhotoCapabilitiesAtom)

	return Boolean(
		currentPhoto &&
		capabilities &&
		currentPhoto.id === capabilities.photoId &&
		capabilities.isSourceMonochrome,
	)
})

/**
 * Internal loading state to track preview and photo loading separately
 * as well as an AbortController to cancel loading
 */
const photoLoadingState = {
	controller: new AbortController(),
	loadingPreviewId: null as null | string,
	loadingPhotoId: null as null | string,
	clearViewportTimer: null as null | ReturnType<typeof setTimeout>,
}

export function clearPhotoLoadingState() {
	photoLoadingState.loadingPhotoId = null
}

const CLEAR_VIEWPORT_DEBOUNCE_MS = 120

function cancelPendingViewportClear() {
	if (photoLoadingState.clearViewportTimer !== null) {
		clearTimeout(photoLoadingState.clearViewportTimer)
		photoLoadingState.clearViewportTimer = null
	}
}

function scheduleViewportClear(photoId: string) {
	cancelPendingViewportClear()
	photoLoadingState.clearViewportTimer = setTimeout(() => {
		photoLoadingState.clearViewportTimer = null
		if (store.get(currentPhotoIdAtom) === photoId) {
			getEditor().clearViewport()
		}
	}, CLEAR_VIEWPORT_DEBOUNCE_MS)
}

export interface LoadCurrentFileOptions {
	kinds: ("preview" | "full")[]
	forceReload?: boolean
	isTransient?: boolean
	onPreviewReady?: () => void
}

export function isCurrentFileLoaded() {
	const currentPhoto = store.get(currentPhotoAtom)
	const editor = getEditor()

	return editor.photo.id === currentPhoto?.id && !editor.isImportPending
}

/**
 * Backfills history records with current default values so that fields
 * introduced after the record was created (e.g. new localAdjustment params)
 * are always present at runtime. Mutates records in place.
 */
function normalizeAdjustmentHistory(
	photo: PhotoRecord,
	history: Awaited<ReturnType<typeof CatalogManager.fetchAdjustmentHistoryRecords>>,
) {
	const defaultAdjustments = getDefaultAdjustments(photo)
	history.forEach((record) => {
		record.adjustments = { ...defaultAdjustments, ...record.adjustments }
		record.adjustments.localAdjustments = record.adjustments.localAdjustments?.map(
			(maskGroup) => ({ ...defaultMaskAdjustments, ...maskGroup }),
		)
	})
}

/**
 * Lightweight sync: only fetches and writes adjustment history to the store.
 * Use this in grid mode to update slider values without the cost of a full photo import.
 */
export async function syncAdjustmentHistory(photo: PhotoRecord) {
	try {
		const history = await CatalogManager.fetchAdjustmentHistoryRecords(photo)

		// Guard against stale responses: if the user has already navigated away, discard.
		if (store.get(currentPhotoIdAtom) !== photo.id) return

		normalizeAdjustmentHistory(photo, history)
		store.set(adjustmentsHistoryAtom, history)
	} catch (e) {
		console.error("Failed to sync adjustment history:", e)
	}
}

export async function loadCurrentFile(
	options: LoadCurrentFileOptions = {
		kinds: ["preview", "full"],
	},
) {
	const editor = getEditor()

	const currentPhoto = store.get(currentPhotoAtom)
	const project = store.get(projectAtom)

	if (!currentPhoto || !project) {
		return
	}

	if (options?.forceReload) {
		photoLoadingState.loadingPreviewId = null
		photoLoadingState.loadingPhotoId = null
	}

	photoLoadingState.controller.abort("abort")
	photoLoadingState.controller = new AbortController()

	cancelPendingViewportClear()

	editor.renderer.profileManager.reset()

	store.set(photoLoadingStateAtom, "loading")
	store.set(photoLoadingErrorAtom, null)
	store.set(photoLoadedDimensionsAtom, null)
	store.set(loadedPhotoCapabilitiesAtom, null)

	if (options.kinds.includes("preview")) {
		editor.setImportPending()

		await loadPreview(
			currentPhoto,
			project.id,
			options.isTransient ?? false,
			options.onPreviewReady,
		)
	}

	if (options.kinds.includes("preview") && options.kinds.includes("full")) {
		await wait(15)
	}

	if (options.kinds.includes("full")) {
		loadPhoto(currentPhoto, photoLoadingState.controller.signal)
	}
}

const loadPreview = async (
	photo: PhotoRecord,
	projectId: string,
	isTransient: boolean,
	onPreviewReady?: () => void,
) => {
	try {
		if (photo.id === photoLoadingState.loadingPreviewId) {
			// Preview is already loading
			return
		}
		if (photo.id === photoLoadingState.loadingPhotoId) {
			// Photo is already loading, no need to load preview
			return
		}

		photoLoadingState.loadingPreviewId = photo.id

		const blob = await fetchPreviewDataOOB(projectId, photo, PreviewSize.medium)

		if (blob) {
			await loadPreviewImage(projectId, photo, blob)
		} else {
			if (
				isTransient ||
				photo.status === "pending" ||
				photo.status === "applying" ||
				store.get(isCullingModeAtom)
			) {
				scheduleViewportClear(photo.id)
			}
		}
	} catch (e) {
		handleLoadPhotoError(photo, e)
	} finally {
		photoLoadingState.loadingPreviewId = null
		onPreviewReady?.()
	}
}

const loadPreviewImage = async (projectId: string, photo: PhotoRecord, previewData: Blob) => {
	const editor = getEditor()
	const currentPhotoId = store.get(currentPhotoIdAtom)
	const previewState = store.get(photosViewAtom)
	const isCulling = store.get(isCullingModeAtom)

	// Our previewData is already in display-p3 color space, and we want to pass it through without conversion; otherwise it will be converted to sRGB
	const image = await createImageBitmap(previewData, { colorSpaceConversion: "none" })

	const imageColorSpace = OutputColorSpace.displayP3

	if (currentPhotoId != photo.id || editor.originalPhoto.id === photo.id) {
		return
	}

	const originalImage = await (async () => {
		if (previewState !== "before/after") {
			return undefined
		}

		if (isCulling) {
			return undefined
		}

		const existingBlob = await fetchOriginalPreviewDataOOB(projectId, photo, PreviewSize.medium)

		if (existingBlob) {
			const image = await createImageBitmap(existingBlob, { colorSpaceConversion: "none" })
			return image
		}

		const file = getFileForPhoto(photo)

		if (!file) {
			return undefined
		}

		const blob = await PreviewLoader.getImage(file, PreviewSize.medium)

		if (!blob) {
			return undefined
		}

		const image = await createImageBitmap(blob, { colorSpaceConversion: "none" })
		return image
	})()

	const originalImageAdjustments = await CatalogManager.fetchAdjustments(photo)

	cancelPendingViewportClear()

	editor.renderPreview({
		image,
		imageColorSpace,
		originalImage,
		originalImageAdjustments,
	})

	image.close()
	originalImage?.close()
}

const loadPhoto = throttleAsync(async (photo: PhotoRecord, signal: AbortSignal) => {
	if (photo.id === photoLoadingState.loadingPhotoId) {
		// Photo is already loading
		return
	}

	photoLoadingState.loadingPhotoId = photo.id

	const measureLoadPhoto = startMeasurement("loadPhoto: total time")

	try {
		const isCulling = store.get(isCullingModeAtom)
		const editor = getEditor()
		const file = getFileForPhoto(photo) ?? (await getFileForPhotoFromOpfsIfNeeded(photo))

		let adjustments = await CatalogManager.fetchAdjustments(photo)

		const cullingInfo = store.get(cullingInfoAtom)[photo.id]
		const isAutoExposureOn = store.get(isCullingAutoExposureOnAtom)
		const isAutoExposureAvailable = !!cullingInfo?.luminanceScoreStats

		if (isCulling && isAutoExposureOn && isAutoExposureAvailable) {
			adjustments = {
				...adjustments,
				...cullingInfo?.luminanceScoreStats?.autoLightParams,
			}
		}

		if (!file) {
			throw new Error("Cannot load photo. File missing")
		}

		signal.throwIfAborted()

		const history = await CatalogManager.fetchAdjustmentHistoryRecords(photo)

		// merge every history items with default adjustments, so that we can prevent 'healingMasks=null' in history item
		normalizeAdjustmentHistory(photo, history)

		store.set(adjustmentsHistoryAtom, history)
		store.set(photoLoadingStateAtom, "loaded")
		store.set(photoLoadingErrorAtom, null)

		updateWhiteBalanceSettingIfNeeded(adjustments)

		signal.throwIfAborted()

		const metadata = getPhotoMetadata(photo.id)

		await editor.import(file, {
			photoId: photo.id,
			metadata,
			adjustments,
			enableHalfResolution: isCulling,
			enableDenoise: !isCulling,
			signal: signal,
		})

		cancelPendingViewportClear()

		store.set(photoLoadedDimensionsAtom, editor.photo.imageState.imageSize)
		store.set(loadedPhotoCapabilitiesAtom, {
			photoId: editor.photo.id,
			isSourceMonochrome: editor.photo.isSourceMonochrome,
		})

		const editorPhotoSize = editor.photo.imageState.imageSize

		const shouldWriteMetadata =
			!!metadata &&
			(metadata?.width !== editorPhotoSize[0] || metadata?.height !== editorPhotoSize[1])

		if (shouldWriteMetadata) {
			updatePhotoMetadata(photo.id, {
				...metadata,
				width: editorPhotoSize[0],
				height: editorPhotoSize[1],
				auxiliaryData: {
					...metadata?.auxiliaryData,
					hasComputedDimensions: true,
				},
			})
		}

		signal.throwIfAborted()

		if (isCulling) {
			editor.render({ reason: "culling-photo-loaded" })

			if (store.get(isCullingAutoExposureOnAtom)) {
				generateAutoExposedPreview(photo.id)
			}
		}

		if (
			!store.get(isCullingModeAtom) &&
			store.get(isCalibrationAtom) &&
			store.get(applyAIAdjustmentsAtom) &&
			photo.historyIndex == null
		) {
			// photo has no adjustments history
			queueApplyAutoAdjustments(photo)
		}

		if (photo.isUnsupported) {
			updatePhoto(photo.id, {
				isUnsupported: false,
			})
		}

		clearPhotoLoadingState()

		signal.throwIfAborted()

		// Only measure successful loads
		measureLoadPhoto()
	} catch (e) {
		handleLoadPhotoError(photo, e)
	}
})

function handleLoadPhotoError(photo: PhotoRecord, error: any) {
	if (isAbortError(error)) {
		clearPhotoLoadingState()
		// Load was canceled
		return
	}

	const isDisplayedLoadingError = photo.id === photoLoadingState.loadingPhotoId

	if (isDisplayedLoadingError) {
		showPhotoError(error)
		store.set(photoLoadingStateAtom, "error")
		store.set(photoLoadingErrorAtom, error)
	}

	if (isUnsupportedFileError(error)) {
		// Unsupported file
		if (!photo.isUnsupported) {
			updatePhoto(photo.id, { isUnsupported: true })
			showUnsupportedTooltip()

			void logPhotoError(error, {
				photoId: photo.id,
				source: "loading",
				logAnalytics: true,
			})
		}
		return
	}

	void logPhotoError(error, {
		photoId: photo.id,
		source: "loading",
		logAnalytics: isDisplayedLoadingError && !isNotFoundError(error),
	})
}
